/**
 * InviteModal — invite-a-user surface (#1419).
 *
 * Ported verbatim from the former `pages/Users.tsx` (only the mount point and
 * import paths change): the invite form, its per-open reset effect, the
 * `inviteUser` call, and the `users.all()` cache invalidation are unchanged.
 * Owned by the UsersPage wrapper, opened from the resource header's invite CTA.
 */
import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalDescription,
} from '@resource-kit/ui/react';
import { useUserManagement } from '@hooks/useAuth';
import { ROLES, ROLE_LABELS } from '@/permissions';
import { queryKeys } from '@/lib/queryKeys';

export function InviteModal({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const { inviteUser } = useUserManagement();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<(typeof ROLES)[number]>('operator');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The Modal component unmounts its children when closed, but this
  // wrapper component stays mounted across open/close — so the
  // useState values survive a Cancel and re-appear in the form on the
  // next open. Reset whenever `open` transitions to true so re-opens
  // present a fresh form (matches user expectation; pinned by an e2e
  // assertion in the `7A.2` case of `e2e/user-admin.spec.ts`).
  useEffect(() => {
    if (open) {
      setEmail('');
      setName('');
      setRole('operator');
      setError('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const result = await inviteUser(email, role, name);
    setSaving(false);
    if (result.success) {
      setEmail('');
      setName('');
      setRole('operator');
      // Colocate cache invalidation with the mutation so a fresh invite
      // shows up in the list without any hand-wired refetch callback.
      // `users.all()` is a prefix of the list key, so the table refetches.
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      onInvited();
      onClose();
    } else {
      setError(result.error || t('users.inviteFailed'));
    }
  };

  return (
    <Modal isOpen={open} size="md">
      <form data-testid="user-create-form" onSubmit={handleSubmit}>
        <ModalHeader onClose={saving ? undefined : onClose}>
          <ModalTitle>{t('users.inviteModalTitle')}</ModalTitle>
          <ModalDescription>{t('users.inviteModalDesc')}</ModalDescription>
        </ModalHeader>
        <ModalBody>
          {error && (
            <div
              data-testid="user-create-error"
              className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('users.emailLabel')}</label>
              <Input
                data-testid="user-create-email"
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('users.emailPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('users.nameLabel')}</label>
              <Input
                data-testid="user-create-name"
                value={name}
                required
                onChange={(e) => setName(e.target.value)}
                placeholder={t('users.namePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('users.roleLabel')}</label>
              <select
                data-testid="user-create-role"
                value={role}
                onChange={(e) => setRole(e.target.value as (typeof ROLES)[number])}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(ROLE_LABELS[r], { defaultValue: ROLE_LABELS[r] })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {t('ui.cancel')}
          </Button>
          <Button data-testid="user-create-submit" type="submit" disabled={saving}>
            {saving ? t('users.sending') : t('users.sendInvite')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
